import React, { useEffect, useState, FC, useCallback } from "react";
import { db } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

interface LetterHeadUploadProps {
  userID: string;
}

interface LetterHeadState {
  file: File | null;
  uploading: boolean;
  errorMessage: string;
  fileUrl: string;
  fileName: string;
  currentFileName: string;
}

const LetterHeadUpload: FC<LetterHeadUploadProps> = ({ userID }) => {
  const [state, setState] = useState<LetterHeadState>({
    file: null,
    uploading: false,
    errorMessage: "",
    fileUrl: "",
    fileName: "template.docx",
    currentFileName: "",
  });

  const fetchCurrentTemplate = useCallback(async () => {
    const storage = getStorage();
    try {
      const userDocRef = doc(db, "users", userID);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.letterheadFileName) {
          setState(prev => ({ ...prev, fileName: userData.letterheadFileName }));
        }
      }

      const fileRef = storageRef(
        storage,
        `docxTemplates/${userID}/${state.fileName}`
      );

      const url = await getDownloadURL(fileRef);
      setState(prev => ({ ...prev, fileUrl: url }));

      // Fetch the .docx file and convert it to HTML
      const response = await fetch(url);
      await response.blob();
    } catch (error) {
      console.error("Error fetching current template:", error);
      // Don't set an error message here, as it's not necessarily an error if no template exists
    }
  }, [userID]);

  useEffect(() => {
    fetchCurrentTemplate();
  }, [fetchCurrentTemplate]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const file = event.target.files[0];
      if (file) {
        if (
          file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
          file.type === "application/msword"
        ) {
          setState(prev => ({ ...prev, file, currentFileName: file.name, errorMessage: "" }));
        } else {
          setState(prev => ({ ...prev, errorMessage: "Please upload a .doc or .docx file." }));
        }
      }
    }
  };

  const handleUpload = async () => {
    if (state.file) {
      try {
        setState(prev => ({ ...prev, uploading: true }));
        const storage = getStorage();
        const fileRef = storageRef(
          storage,
          `docxTemplates/${userID}/${state.currentFileName}`
        );

        await uploadBytes(fileRef, state.file);
        const downloadUrl = await getDownloadURL(fileRef);

        const userDocRef = doc(db, "users", userID);
        await updateDoc(userDocRef, {
          letterheadFileName: state.currentFileName
        });

        setState(prev => ({ ...prev, uploading: false, fileName: state.currentFileName, currentFileName: "", fileUrl: downloadUrl }));
        alert("Letterhead uploaded successfully!");
      } catch (error) {
        console.error("Error uploading the file:", error);
        setState(prev => ({ 
          ...prev, 
          uploading: false, 
          errorMessage: "Failed to upload letterhead. Please try again." 
        }));
      }
    }
  };

  return (
    <div className="letterhead-upload" style={{ padding: "1rem" }}>
      <label
        htmlFor="letterhead-upload"
        className="letterhead-upload__label"
        style={{ display: "block", fontWeight: "bold" }}
      >
        Upload your letterhead (DOC or DOCX format)
      </label>
      <div className="letterhead-upload__description" style={{ fontSize: "0.8rem", marginBottom: "1rem" }}>
        Personalise your letters using your company's letterhead.
      </div>

      <input
        id="letterhead-upload"
        type="file"
        accept=".doc, .docx"
        onChange={handleFileChange}
        disabled={state.uploading}
        aria-describedby="file-upload-error"
      />
      <button
        className="button-small-brand-green letterhead-upload__button"
        style={{ padding: "0.8rem" }}
        onClick={handleUpload}
        disabled={state.uploading || !state.file || !!state.errorMessage}
        aria-busy={state.uploading}
      >
        {state.uploading ? "Uploading..." : "Upload"}
      </button>
      {state.errorMessage && (
        <div id="file-upload-error" className="letterhead-upload__error" style={{ color: "red" }} role="alert">
          {state.errorMessage}
        </div>
      )}

      {state.fileUrl && (
        <div className="letterhead-upload__current-template">
          <br />
          <div style={{ fontSize: "0.8rem" }}>
            Current letterhead: {state.fileName}
          </div>
          <div style={{ fontSize: "0.8rem" }}>
            <a href={state.fileUrl} download>
              Download current letterhead
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default LetterHeadUpload;